import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Droplet, Hash, Settings2, Globe2 } from "lucide-react";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import { siteConfig } from "@/config/siteConfig";

const PRINCIPLES = [
  {
    key: "specialisation",
    title: "Specialisation",
    body:
      "A focused portfolio designed around pharmaceutical printing, coding and related production requirements.",
  },
  {
    key: "technical",
    title: "Technical Approach",
    body:
      "Product recommendations begin with understanding the application — machine, substrate, print requirement — rather than simply supplying a catalogue item.",
  },
  {
    key: "partnerships",
    title: "Partnerships",
    body:
      "We aim to develop long-term relationships with pharmaceutical manufacturers, distribution partners and specialist manufacturing partners in the markets we serve.",
  },
];

const FOCUS = [
  {
    key: "pharma-printing",
    Icon: Droplet,
    title: "Pharmaceutical Printing",
    body: "Specialised inks and supporting products for pharmaceutical printing applications.",
  },
  {
    key: "coding-marking",
    Icon: Hash,
    title: "Coding & Marking",
    body: "Selected consumables supporting batch, lot and variable-information coding.",
  },
  {
    key: "application-support",
    Icon: Settings2,
    title: "Application Support",
    body: "Understanding equipment, substrate and production requirements before product evaluation.",
  },
  {
    key: "international-distribution",
    Icon: Globe2,
    title: "International Distribution",
    body: "Building relationships with manufacturers and qualified distribution partners across international markets.",
  },
];

const IMG_BASE = "/images/home";

const About = () => {
  const path = "/about";
  const canonical = `${siteConfig.DOMAIN}${path}`;
  const schema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: `About ${siteConfig.COMPANY_NAME}`,
    description:
      "A Dubai-based international supplier connecting specialist pharmaceutical printing, coding and production solutions with manufacturers and distribution partners worldwide.",
    url: canonical,
  };

  return (
    <>
      <SEO
        rawTitle={`About ${siteConfig.COMPANY_NAME} — Pharmaceutical Printing & Coding Solutions`}
        description="A Dubai-based international supplier of specialist pharmaceutical printing, coding and production consumables, sourced through manufacturing partners."
        path={path}
        extraSchema={schema}
      />
      <Helmet />

      <div className="mx-auto max-w-[1240px] px-6 lg:px-10 pt-8 lg:pt-10">
        <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "About" }]} />
      </div>

      {/* Intro — 50/50 (§13) */}
      <section
        className="mx-auto max-w-[1240px] px-6 lg:px-10 pt-8 lg:pt-10 pb-16 lg:pb-20"
        data-testid="about-hero"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          <div>
            <p className="eyebrow">About {siteConfig.COMPANY_NAME}</p>
            <h1
              className="font-heading mt-4 text-[36px] leading-tight lg:text-[48px] lg:leading-[1.05] font-semibold text-[var(--navy)]"
              data-testid="about-h1"
            >
              Connecting Specialist Solutions With Pharmaceutical Manufacturers Worldwide
            </h1>
            <div className="mt-8 space-y-5 text-[16.5px] leading-relaxed text-[var(--charcoal)]">
              <p data-testid="about-p1">
                {siteConfig.COMPANY_NAME} is a Dubai-based international business
                focused on specialised printing, coding and production solutions
                for pharmaceutical manufacturing.
              </p>
              <p data-testid="about-p2">
                We work with specialist manufacturing and supply partners to make
                carefully selected products available to pharmaceutical
                manufacturers and distribution partners across international
                markets.
              </p>
              <p data-testid="about-p3">
                Our approach is application-led. Rather than simply supplying a
                catalogue product, we aim to understand the printing process,
                equipment, substrate and production requirement before
                recommending a solution for evaluation.
              </p>
            </div>
          </div>
          <div className="border border-[var(--border)] rounded-[4px] overflow-hidden bg-[var(--bg-alt)]" data-testid="about-hero-image">
            <picture>
              <source type="image/webp" srcSet={`${IMG_BASE}/blister-foil-close-up.webp`} />
              <img
                src={`${IMG_BASE}/blister-foil-close-up.jpg`}
                alt="Pharmaceutical manufacturing facility interior with packaging equipment"
                loading="lazy"
                decoding="async"
                width="1264"
                height="848"
                className="w-full h-auto block"
              />
            </picture>
          </div>
        </div>
      </section>

      {/* Principles */}
      <section
        className="mx-auto max-w-[1240px] px-6 lg:px-10 py-14 lg:py-20 border-t border-[var(--border)]"
        data-testid="about-principles"
      >
        <p className="eyebrow">Our Principles</p>
        <h2 className="font-heading mt-4 text-2xl lg:text-[32px] font-semibold text-[var(--navy)]">
          Three principles shape how we work
        </h2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10">
          {PRINCIPLES.map((p) => (
            <div key={p.key} data-testid={`about-principle-${p.key}`}>
              <h3 className="font-heading text-[24px] font-semibold text-[var(--navy)]">{p.title}</h3>
              <p className="mt-4 text-[15.5px] leading-relaxed text-[var(--slate)]">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* What We Focus On (§14) */}
      <section
        className="mx-auto max-w-[1240px] px-6 lg:px-10 py-14 lg:py-20 border-t border-[var(--border)]"
        data-testid="about-focus"
      >
        <p className="eyebrow">What We Focus On</p>
        <h2 className="font-heading mt-4 text-2xl lg:text-[32px] font-semibold text-[var(--navy)]">
          What We Focus On
        </h2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
          {FOCUS.map(({ key, Icon, title, body }) => (
            <div
              key={key}
              className="border border-[var(--border)] rounded-[4px] bg-white p-8"
              data-testid={`about-focus-${key}`}
            >
              <Icon className="w-9 h-9 text-[var(--pharma-blue)]" strokeWidth={1.5} aria-hidden="true" />
              <h3 className="font-heading mt-5 text-[20px] font-semibold text-[var(--navy)]">{title}</h3>
              <p className="mt-3 text-[15px] leading-relaxed text-[var(--slate)]">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Mission / Vision (§15) — larger cards, subtle blue left border */}
      <section className="bg-[var(--bg-alt)]" data-testid="about-mission-vision">
        <div className="mx-auto max-w-[1240px] px-6 lg:px-10 py-14 lg:py-20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <article
              className="border border-[var(--border)] rounded-[4px] p-10 lg:p-12 bg-white"
              style={{ borderLeft: "3px solid var(--pharma-blue)" }}
              data-testid="about-mission"
            >
              <p className="font-heading text-[12px] font-semibold uppercase text-[var(--pharma-blue)]" style={{ letterSpacing: "0.12em" }}>
                Mission
              </p>
              <p className="font-heading mt-4 text-[26px] leading-snug font-semibold text-[var(--navy)]">
                To become a trusted international partner for specialised pharmaceutical printing and coding solutions.
              </p>
            </article>
            <article
              className="border border-[var(--border)] rounded-[4px] p-10 lg:p-12 bg-white"
              style={{ borderLeft: "3px solid var(--pharma-blue)" }}
              data-testid="about-vision"
            >
              <p className="font-heading text-[12px] font-semibold uppercase text-[var(--pharma-blue)]" style={{ letterSpacing: "0.12em" }}>
                Vision
              </p>
              <p className="font-heading mt-4 text-[26px] leading-snug font-semibold text-[var(--navy)]">
                To build a strong international distribution network connecting reliable technical products with pharmaceutical manufacturers worldwide.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* Bottom CTA band (§16) */}
      <section className="bg-[var(--navy)] text-white" data-testid="about-cta">
        <div className="mx-auto max-w-[1240px] px-6 lg:px-10 py-16 lg:py-24">
          <p className="text-[12px] uppercase tracking-[0.14em] text-white/70 font-semibold">Get In Touch</p>
          <h2 className="font-heading mt-4 text-[32px] lg:text-[40px] leading-tight font-semibold">
            Let&rsquo;s Discuss Your Requirement
          </h2>
          <p className="mt-6 text-[16.5px] leading-relaxed text-white/85 max-w-3xl">
            Whether you&rsquo;re a pharmaceutical manufacturer evaluating an alternative printing solution or a distributor interested in representing our portfolio, we&rsquo;d like to hear from you.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row flex-wrap gap-3">
            <Link
              to="/contact?type=technical"
              className="inline-flex items-center justify-center px-6 py-3 rounded-[4px] text-sm font-semibold uppercase tracking-wider bg-white text-[var(--navy)] hover:bg-white/90 transition-colors"
              data-testid="about-cta-primary"
            >
              Discuss Your Application
            </Link>
            <Link
              to="/distributors"
              className="btn-outline-invert text-sm px-6 py-3"
              data-testid="about-cta-secondary"
            >
              Become a Distributor
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default About;
